import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { classNames } from '@panwds/tailwindcss';
import React, { useCallback, useState } from 'react';
import { useTableLayoutEnabled } from '../layouts/table_layout.js';
import { arrayMoveImmutable } from '../utils/arrayMove.js';
import { groupColumns } from './_grouping.js';
import { HeaderCell } from './header_cell.js';
export function HeaderRow({ appearance = 'primary', uppercaseHeaderColumns = false, disableColumnOrder = false, headerGroup, setColumnOrder }) {
    const { sortRowColumn, selectRowColumn, groupedColumns, leftStickyColumns, generalColumns, rightStickyColumns } = groupColumns(headerGroup.headers);
    const tableLayoutEnabled = useTableLayoutEnabled();
    const sensors = useSensors(useSensor(MouseSensor, {
        // Require the mouse to move by 10 pixels before activating.
        // Slight distance prevents sortable logic messing with
        // interactive elements in the handler toolbar component.
        activationConstraint: {
            distance: 10
        }
    }), useSensor(TouchSensor, {
        // Press delay of 250ms, with tolerance of 5px of movement.
        activationConstraint: {
            delay: 250,
            tolerance: 5
        }
    }));
    const [draggedColumnId, setDraggedColumnId] = useState(null);
    const getIndex = useCallback((id) => generalColumns.findIndex((c) => c.id === id), [generalColumns]);
    /* istanbul ignore next */
    function onSortEnd({ oldIndex, newIndex }) {
        const next = groupedColumns
            .map((c) => c.id)
            .concat(leftStickyColumns.map((c) => c.id))
            .concat(arrayMoveImmutable(generalColumns.map((c) => c.id), oldIndex, newIndex))
            .concat(rightStickyColumns.map((c) => c.id));
        setColumnOrder(next);
    }
    const width = tableLayoutEnabled ? { width: undefined } : {};
    const addBorder = (appearance === 'primary' || appearance === 'secondary') && !tableLayoutEnabled;
    const className = classNames('twds-group', 'twds-box-content', addBorder &&
        'twds-border-solid twds-border-gray-300 twds-border-b-0 twds-border-r twds-border-t twds-border-l dark:twds-border-gray-950');
    const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps({
        style: {
            display: 'flex',
            minHeight: appearance === 'primary' ? '2rem' : '1rem',
            marginBottom: tableLayoutEnabled ? '-1px' : '',
            ...width
        }
    });
    return (React.createElement("div", { key: key, ...headerGroupProps, className: className },
        !!selectRowColumn.length && (React.createElement("div", { className: "twds-flex" }, selectRowColumn.map((column, index) => (React.createElement(HeaderCell, { uppercaseHeaderColumns: uppercaseHeaderColumns, column: column, key: column.id, index: index }))))),
        !!groupedColumns.length && (React.createElement("div", { className: "twds-flex" }, groupedColumns.map((column, index) => (React.createElement(HeaderCell, { uppercaseHeaderColumns: uppercaseHeaderColumns, column: column, key: column.id, index: index }))))),
        !!leftStickyColumns.length && (React.createElement("div", { className: "twds-flex twds-shadow-left dark:twds-shadow-dark-left", style: {
                background: 'inherit',
                position: 'sticky',
                left: 0,
                zIndex: 2
            } }, leftStickyColumns.map((column, index) => (React.createElement(HeaderCell, { stickyLeft: true, uppercaseHeaderColumns: uppercaseHeaderColumns, column: column, key: column.id, index: index }))))),
        React.createElement("div", { className: "twds-flex twds-flex-grow" },
            React.createElement(DndContext, { sensors: sensors, autoScroll: false, onDragStart: ({ active }) => {
                    if (active) {
                        setDraggedColumnId(String(active.id));
                    }
                }, onDragEnd: ({ active, over }) => {
                    if (over && active.id !== over.id) {
                        onSortEnd({
                            oldIndex: getIndex(active.id),
                            newIndex: getIndex(over.id)
                        });
                    }
                } },
                React.createElement(SortableContext, { items: generalColumns.map((c) => c.id), strategy: horizontalListSortingStrategy }, generalColumns.map((column, index) => (React.createElement(HeaderCell, { uppercaseHeaderColumns: uppercaseHeaderColumns, column: column, key: column.id, orderable: !disableColumnOrder, index: index, isDragging: draggedColumnId === column.id, lastCell: generalColumns.length === index + 1 })))))),
        !!rightStickyColumns.length && (React.createElement("div", { className: "twds-flex twds-shadow-right dark:twds-shadow-dark-right", style: {
                background: 'inherit',
                position: 'sticky',
                right: 0
            } }, rightStickyColumns.map((column, index) => (React.createElement(HeaderCell, { stickyRight: true, uppercaseHeaderColumns: uppercaseHeaderColumns, column: column, key: column.id, index: index, lastGroup: tableLayoutEnabled }))))),
        !!sortRowColumn.length && (React.createElement("div", { className: "twds-flex" }, sortRowColumn.map((column, index) => (React.createElement(HeaderCell, { uppercaseHeaderColumns: uppercaseHeaderColumns, column: column, key: column.id, index: index })))))));
}
//# sourceMappingURL=header_row.js.map